<script setup>
import { ref, computed } from 'vue';
import IconHeaderBack from '@/components/svg/IconHeaderBack.vue';
import IconHeaderClose from '@/components/svg/IconHeaderClose.vue';
import { useClientType } from '@/composables/useClientType';
import { usePaymentsStore } from '@/store/modules/payments';

/**
 * 정기구독 해지 화면 (시연 v2.1 cancel-screen).
 * - 모바일: 풀스크린(슬라이드인) / PC: 중앙 팝업
 * - Hero(만류 메시지) + 안내 callout + 해지 사유 선택 → confirm 모달 → confirm emit
 * - 부모에서 v-if로 마운트하고 close/confirm emit으로 처리
 * - "정상 이용 가능" 날짜는 활성 구독의 accessEndDate(실제 데이터)에서 가져옴
 */
const { isMobileUi } = useClientType();
const paymentsStore = usePaymentsStore();

// embedded: 풀스크린/팝업 대신 탭/페이지에 인라인으로 폼만 렌더 (구독관리 '구독 해지' 탭)
defineProps({ embedded: { type: Boolean, default: false } });

const emit = defineEmits(['close', 'confirm']);

/** 이용 종료일(accessEndDate) → "YYYY년 M월 D일" */
const nextPaymentDate = computed(() => {
  const d = paymentsStore.activeSubscription?.accessEndDate;
  if (!d) return '';
  const [y, m, day] = String(d).slice(0, 10).split('-');
  return `${y}년 ${Number(m)}월 ${Number(day)}일`;
});

const REASONS = [
  { id: 'other-service', text: '다른 서비스를 사용하고 있어요' },
  { id: 'low-usage', text: '잘 활용하지 못했어요' },
  { id: 'pause', text: '잠시 쉬어가고 싶어요' },
  { id: 'price', text: '가격이 부담돼요' },
  { id: 'other', text: '그 외' },
];

const selectedReason = ref(null);
const showConfirmModal = ref(false);

const canConfirm = computed(() => !!selectedReason.value);

const selectReason = (id) => {
  selectedReason.value = id;
};

const openConfirm = () => {
  if (!canConfirm.value) return;
  showConfirmModal.value = true;
};

const cancelConfirm = () => {
  showConfirmModal.value = false;
};

const doConfirm = () => {
  showConfirmModal.value = false;
  const selected = REASONS.find((r) => r.id === selectedReason.value);
  emit('confirm', selected?.text ?? '');
};
</script>

<template>
  <div v-if="!isMobileUi && !embedded" class="cancel-backdrop" @click="emit('close')" />
  <div
    class="cancel-screen"
    :class="
      embedded
        ? 'cancel-screen--embedded'
        : isMobileUi
          ? 'cancel-screen--mobile'
          : 'cancel-screen--pc'
    "
  >
    <div v-if="!embedded" class="app-header-cancel">
      <button
        type="button"
        class="back-btn"
        :aria-label="isMobileUi ? '뒤로' : '닫기'"
        @click="emit('close')"
      >
        <IconHeaderBack v-if="isMobileUi" />
        <IconHeaderClose v-else />
      </button>
      <span class="header-title">구독 해지</span>
    </div>

    <div class="cancel-body">
      <div class="cancel-hero">
        <div class="cancel-hero-emoji">🌙</div>
        <div class="cancel-hero-title">정말 구독 해지하시겠어요?</div>
        <div class="cancel-hero-message">
          <strong>할루시네이션 없는 정통 명리 해석</strong>을<br />
          제공해드리기 위해 노력하고 있어요
        </div>
        <div class="cancel-hero-tail">언제든 필요하실 때 다시 신청해주세요</div>
      </div>

      <div class="info-callout">
        <span class="info-callout-icon">💡</span>
        <div class="info-callout-text">
          <div class="info-line">
            <strong>{{ nextPaymentDate }}</strong
            >까지는 정상 이용 가능해요
          </div>
          <div class="info-line">
            구독 기간 중 본 분석 결과는 <strong>1년간 다시 볼 수 있어요</strong>
            <span class="info-sub">(일부 서비스 제외)</span>
          </div>
        </div>
      </div>

      <div class="cancel-section-title">
        해지하시는 이유를 알려주세요
        <span class="optional">(선택 시 해지 가능)</span>
      </div>
      <div class="reason-list">
        <div
          v-for="r in REASONS"
          :key="r.id"
          class="reason-item"
          :class="{ active: selectedReason === r.id }"
          @click="selectReason(r.id)"
        >
          <div class="reason-radio" />
          <span class="reason-text">{{ r.text }}</span>
        </div>
      </div>
    </div>

    <div class="cancel-cta-area">
      <button type="button" class="cta-btn cta-btn-secondary" @click="emit('close')">
        계속 이용하기
      </button>
      <button
        type="button"
        class="cta-btn cta-btn-primary"
        :disabled="!canConfirm"
        @click="openConfirm"
      >
        해지하기
      </button>
    </div>

    <!-- 최종 확인 모달 -->
    <template v-if="showConfirmModal">
      <div class="modal-backdrop" @click="cancelConfirm" />
      <div class="cancel-modal" @click.stop>
        <div class="cancel-modal-emoji">🌙</div>
        <div class="cancel-modal-title">해지 신청을 완료할까요?</div>
        <div class="cancel-modal-text">
          <strong>{{ nextPaymentDate }}</strong
          >까지는 정상 이용 가능해요.<br />
          언제든 다시 신청하실 수 있어요.
        </div>
        <div class="cancel-modal-buttons">
          <button type="button" class="cta-btn cta-btn-secondary" @click="cancelConfirm">
            취소
          </button>
          <button type="button" class="cta-btn cta-btn-primary" @click="doConfirm">
            신청 완료
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.cancel-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: rgba(0, 0, 0, 0.5);
  animation: cancel-fade 0.2s ease-out;
}

.cancel-screen {
  position: fixed;
  z-index: 1101;
  background: #fff;
  display: flex;
  flex-direction: column;
}

/* 모바일: 풀스크린 슬라이드인 */
.cancel-screen--mobile {
  inset: 0;
  padding-top: var(--safe-area-top, 0px);
  animation: slide-in-right 0.3s ease-out;
}

/* PC: 중앙 팝업 */
.cancel-screen--pc {
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 48px);
  max-width: 420px;
  max-height: 86vh;
  padding-top: 8px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  animation: cancel-scale-in 0.22s ease-out;
}

/* 인라인(탭 내장): 고정 위치/그림자/애니메이션 제거하고 페이지 흐름에 배치 */
.cancel-screen--embedded {
  position: static;
  inset: auto;
  transform: none;
  width: 100%;
  max-width: none;
  max-height: none;
  padding: 0;
  border-radius: 0;
  box-shadow: none;
  overflow: visible;
  animation: none;
  background: transparent;
}

.cancel-screen--embedded .cancel-body {
  overflow: visible;
  background: transparent;
  padding: 0;
}

.cancel-screen--embedded .cancel-cta-area {
  position: static;
  padding: 16px 0 0;
  border-top: none;
  background: transparent;
}

@keyframes cancel-fade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slide-in-right {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

@keyframes cancel-scale-in {
  from {
    transform: translate(-50%, -50%) scale(0.94);
    opacity: 0;
  }
  to {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
}

.app-header-cancel {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 16px 16px 24px;
  background: #fff;
  border-bottom: 1px solid #f3f4f6;
  flex-shrink: 0;
}

/* 모바일: 좌우 16px (body와 동일) */
.cancel-screen--mobile .app-header-cancel {
  padding: 16px;
}

/* PC 팝업: 좌우 32px (body와 동일) */
.cancel-screen--pc .app-header-cancel {
  padding: 16px 32px;
}

.back-btn {
  // width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;

  :deep(.icon) {
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
  }
}

.header-title {
  font-size: 16px;
  line-height: 32px;
  font-weight: 700;
  color: #1f2937;
}

/* PC: 닫기(×)는 헤더 오른쪽 (모바일 '뒤로'는 왼쪽 유지) */
.cancel-screen--pc .header-title {
  order: 1;
}
.cancel-screen--pc .back-btn {
  order: 2;
  margin-left: auto;
}

.cancel-body {
  flex: 1;
  overflow-y: auto;
  background: #f9fafb;
}

/* 모바일: 하단 고정 CTA 공간 확보 */
.cancel-screen--mobile .cancel-body {
  padding: 16px 16px calc(var(--safe-area-bottom, 0px) + 100px);
}

/* PC 팝업: body 좌우 32px */
.cancel-screen--pc .cancel-body {
  padding: 16px 32px;
}

.cancel-hero {
  text-align: center;
  margin-bottom: 16px;
}

.cancel-hero-emoji {
  font-size: 40px;
  margin-bottom: 10px;
  line-height: 1;
}

.cancel-hero-title {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 14px;
  line-height: 1.4;
}

.cancel-hero-message {
  font-size: 13px;
  color: #4b5563;
  line-height: 1.65;
  margin-bottom: 10px;

  strong {
    color: #1e3a5f;
    font-weight: 700;
  }
}

.cancel-hero-tail {
  font-size: 12.5px;
  color: #6b7280;
  font-weight: 500;
}

.info-callout {
  background: #f0f7ff;
  border: 1px solid rgba(30, 58, 95, 0.08);
  border-radius: 10px;
  padding: 14px;
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-bottom: 16px;
}

.info-callout-icon {
  font-size: 14px;
  flex-shrink: 0;
  margin-top: 1px;
}

.info-callout-text {
  flex: 1;
  font-size: 12.5px;
  color: #1e3a5f;
  line-height: 1.55;
  font-weight: 500;
}

.info-line {
  padding: 2px 0;

  strong {
    font-weight: 700;
  }
}

.info-sub {
  font-size: 11px;
  color: #6b7280;
  font-weight: 400;
  margin-left: 2px;
}

.cancel-section-title {
  font-size: 13.5px;
  font-weight: 700;
  color: #1f2937;
  margin: 18px 4px 10px;

  .optional {
    font-size: 11.5px;
    color: #6b7280;
    font-weight: 500;
    margin-left: 2px;
  }
}

.reason-list {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
}

.reason-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.15s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f9fafb;
  }

  &.active {
    .reason-radio {
      border-color: #1e3a5f;

      &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 9px;
        height: 9px;
        border-radius: 50%;
        background: #1e3a5f;
      }
    }

    .reason-text {
      font-weight: 600;
    }
  }
}

.reason-radio {
  position: relative;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1.5px solid #d1d5db;
  box-sizing: border-box;
  flex-shrink: 0;
  transition: all 0.2s;
}

.reason-text {
  flex: 1;
  font-size: 13.5px;
  color: #1f2937;
  font-weight: 500;
}

.cancel-cta-area {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  background: #fff;
  border-top: 1px solid #f3f4f6;
}

/* 모바일: 화면 하단 고정 */
.cancel-screen--mobile .cancel-cta-area {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 12px 16px calc(var(--safe-area-bottom, 0px) + 16px);
}

/* PC 팝업: CTA 좌우 32px + 하단 32px */
.cancel-screen--pc .cancel-cta-area {
  padding: 12px 32px 32px;
}

.cta-btn {
  flex: 1;
  height: 48px;
  border-radius: 12px;
  font-size: 14.5px;
  font-weight: 700;
  font-family: inherit;
  letter-spacing: -0.3px;
  cursor: pointer;
  border: none;
  transition: background 0.15s;
}

.cta-btn-primary {
  background: #1e3a5f;
  color: #fff;

  &:hover:not(:disabled) {
    background: #2c4a75;
  }

  &:disabled {
    background: #e5e7eb;
    color: #9ca3af;
    cursor: not-allowed;
  }
}

.cta-btn-secondary {
  background: #f3f4f6;
  color: #4b5563;

  &:hover {
    background: #e5e7eb;
  }
}

/* 최종 확인 모달 */
.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: rgba(0, 0, 0, 0.6);
  animation: fadeIn 0.2s ease-out;
}

.cancel-modal {
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 1201;
  width: calc(100% - 48px);
  max-width: 320px;
  background: #fff;
  border-radius: 18px;
  padding: 28px 22px 20px;
  text-align: center;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  animation: scaleIn 0.22s ease-out;
}

/* 모바일: 좌우·하단 16px */
.cancel-screen--mobile .cancel-modal {
  padding: 28px 16px 16px;
}

/* PC: 좌우·하단 32px */
.cancel-screen--pc .cancel-modal {
  padding: 28px 32px 32px;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes scaleIn {
  from {
    transform: translate(-50%, -50%) scale(0.92);
    opacity: 0;
  }
  to {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
}

.cancel-modal-emoji {
  font-size: 38px;
  line-height: 1;
  margin-bottom: 14px;
}

.cancel-modal-title {
  font-size: 17px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 12px;
}

.cancel-modal-text {
  font-size: 13px;
  color: #4b5563;
  line-height: 1.65;
  margin-bottom: 22px;

  strong {
    color: #1e3a5f;
    font-weight: 700;
  }
}

.cancel-modal-buttons {
  display: flex;
  gap: 8px;
}
</style>
